/**
 * THE AFTER-ACTION REPORT AS A FILE SOMEBODY SIGNS — 3.19 Learning & Governance
 * spine, A1.
 *
 * WHY A PDF. The retrospective is written and edited in the app as rich text, but
 * a document that closes a project is signed off like any other — and a signature
 * is against a fixed file, not a living document (#200). This produces the report
 * the screen shows as a fixed PDF the sign-off engine can fingerprint, exactly as
 * the charter and the requirements document are produced.
 *
 * WHAT IT IS SHAPED LIKE. The same professional page the charter and requirements
 * draw — a logo above the title, numbered sections each opening with a filled band
 * (R18) — so every document a project sends out reads as one product. The four
 * sections are the report's own: what we set out to do, what we delivered, what
 * moved and why, and what to sustain and improve. The first three are rich text
 * flattened to their lines (bullets kept), because a signable document is plain
 * Helvetica and does not need the editor's full formatting to be read and approved;
 * the fourth prints each line under its kind — what worked, to improve, or a
 * recommendation — so the lessons carried into the next project are named on the
 * report's face.
 *
 * TAKES WHAT IT PRINTS, READS NO CLOCK. The caller hands the composed AarContent and
 * a few facts (title, board, the prepared date, the standing in words). This lays it
 * out and reaches for nothing outside them, so the harder half can be tested without
 * a PDF reader and THE SAME DOCUMENT ALWAYS PRODUCES THE SAME BYTES — a regenerated
 * report matches the fingerprint it was signed against.
 *
 * PLAIN HELVETICA, NO EMBEDDED FONT — one of the fourteen faces every reader has, so
 * the file stays small and opens the same everywhere, the same choice the charter and
 * requirements make and for the same reason.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { JSONContent } from '@tiptap/core';
import { winAnsi, unwrap } from './pdf-text';
import { drawLetterhead, drawBand, wordedFromSlashed, preparedSegments, BRAND, type Letterhead } from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';
import { AAR_LINE_KIND_LABELS, LESSON_KIND_LABELS, hasRecommendation, type Lesson } from './lessons';
import { richText } from './rich-text';
import { aarHeading, AAR_SUSTAIN_EMPTY, scheduleVarianceLine, scheduleVarianceContext, costVarianceLine, costVarianceContext, AAR_BENEFIT_STATUS_LABELS } from './aar';
import type { AarContent } from './types';

/** A4 in points, which is what pdf-lib measures in. */
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;
const FOOT = 38;
const HEAD = 36;

const INK = rgb(0.1, 0.13, 0.16);
const QUIET = rgb(0.42, 0.46, 0.5);
const RULE = rgb(0.8, 0.83, 0.85);

/** The customer's mark, passed in as bytes so this file reaches for nothing. */
export interface Logo {
  bytes: ArrayBuffer | Uint8Array;
  type: 'png' | 'jpg';
}

export interface AarFacts {
  title: string;
  board: string;
  /** Stamped in, never read from the clock here, so the same document twice is the
   *  same bytes and a test can say what the date will be. */
  preparedOn: string;
  /** Who produced it — the signed-in person, named on the letterhead just before
   *  the prepared-on date. Stamped in at produce time, like the date. */
  preparedBy?: string;
  /** The document's standing in words — "Draft", "Awaiting sign-off", "Signed off". */
  status: string;
  logo?: Logo;
  /** The lessons captured on this project during it — consolidated into the
   *  "what to sustain and improve" section so the signed record is complete, not
   *  blank when lessons were logged. A point-in-time snapshot at produce time. */
  lessons?: Lesson[];
}

/** CPFC write 07/07/2026. Day first, which is what the Caribbean and the UK read. */
export const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Words that fit, measured in the font they will be drawn in — wrapping by counting
 * characters is what makes a generated document look generated. A word longer than
 * the whole measure overhangs rather than being dropped.
 */
function wrap(text: string, font: PDFFont, size: number, measure: number): string[] {
  const lines: string[] = [];
  for (const paragraph of unwrap(winAnsi(text)).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const tried = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
      else {
        if (line) lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

/* ── FLATTENING A RICH-TEXT FIELD TO LINES ─────────────────────────────────────
 *
 * The three composed sections and each sustain line are ProseMirror documents. A
 * signable PDF is plain Helvetica, so this walks the document and returns each block
 * as a line, keeping a bullet or number marker so a list still reads as a list.
 * Nested content is flattened rather than dropped — losing a line from a signed
 * document is worse than losing its indentation.
 */
interface Line {
  text: string;
  marker?: string;
  indent?: number;
}

/** The inline text of one block — text nodes joined, a hard break becoming a space. */
function inline(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return ' ';
  return (node.content ?? []).map(inline).join('');
}

function flatten(node: JSONContent, depth: number, out: Line[]): void {
  switch (node.type) {
    case 'paragraph':
    case 'heading': {
      const text = inline(node).trim();
      if (text) out.push({ text, indent: depth * 14 });
      break;
    }
    case 'bulletList':
      for (const li of node.content ?? []) listItem(li, depth, '•', out);
      break;
    case 'orderedList': {
      let n = (node.attrs?.start as number) ?? 1;
      for (const li of node.content ?? []) listItem(li, depth, `${n++}.`, out);
      break;
    }
    case 'blockquote':
      for (const child of node.content ?? []) flatten(child, depth + 1, out);
      break;
    default:
      for (const child of node.content ?? []) flatten(child, depth, out);
  }
}

function listItem(li: JSONContent, depth: number, marker: string, out: Line[]): void {
  const kids = li.content ?? [];
  kids.forEach((child, i) => {
    if (i === 0 && (child.type === 'paragraph' || child.type === 'heading')) {
      const text = inline(child).trim();
      out.push({ text: text || ' ', marker, indent: depth * 14 + 14 });
    } else {
      flatten(child, depth + 1, out);
    }
  });
}

function richLines(doc?: JSONContent | null): Line[] {
  const out: Line[] = [];
  for (const node of doc?.content ?? []) flatten(node, 0, out);
  return out;
}

/** What pdf-lib hands back from embedPng/embedJpg. */
type Art = Parameters<PDFPage['drawImage']>[0] & { width: number; height: number };
interface Embedded {
  art: Art;
  width: number;
  height: number;
}

/** A logo that will not embed is skipped, not fatal — the document is the point. */
async function embed(pdf: PDFDocument, logo?: Logo): Promise<Embedded | undefined> {
  if (!logo) return undefined;
  try {
    const art = logo.type === 'png' ? await pdf.embedPng(logo.bytes) : await pdf.embedJpg(logo.bytes);
    return { art, width: art.width, height: art.height };
  } catch {
    return undefined;
  }
}

interface Running {
  title: string;
  logo?: Embedded;
}

/**
 * A pen that knows where it is on the page and starts a new one when it runs out —
 * the same closure the charter and requirements draw with, so every document shares
 * a look.
 */
function makePen(pdf: PDFDocument, regular: PDFFont, bold: PDFFont, running: Running, titleFont?: PDFFont) {
  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN;
  let section = 0;

  const carryOver = () => {
    const top = y;
    const tall = 13;
    let x = MARGIN;
    if (running.logo) {
      const wide = (running.logo.width / running.logo.height) * tall;
      const scale = Math.min(1, WIDTH / 3 / wide);
      page.drawImage(running.logo.art, { x, y: top - tall * scale, width: wide * scale, height: tall * scale });
      x += wide * scale + 9;
    }
    page.drawText(winAnsi(running.title), { x, y: top - tall + 3.5, size: 8.5, font: regular, color: QUIET });
    page.drawLine({
      start: { x: MARGIN, y: top - tall - 9 },
      end: { x: MARGIN + WIDTH, y: top - tall - 9 },
      thickness: 0.75,
      color: RULE,
    });
    y = top - HEAD;
  };

  const room = (needed: number) => {
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
    carryOver();
  };

  const api = {
    down: (by: number) => {
      y -= by;
    },

    text(
      body: string,
      opts: { size?: number; bold?: boolean; colour?: typeof INK; indent?: number; gap?: number; marker?: string } = {}
    ) {
      const size = opts.size ?? 10;
      const font = opts.bold ? bold : regular;
      const indent = opts.indent ?? 0;
      const leading = size * 1.5;
      const lines = wrap(body, font, size, WIDTH - indent);
      lines.forEach((line, at) => {
        room(leading);
        y -= leading;
        if (at === 0 && opts.marker) {
          page.drawText(winAnsi(opts.marker), { x: MARGIN + indent - 14, y, size, font: regular, color: QUIET });
        }
        page.drawText(line, { x: MARGIN + indent, y, size, font, color: opts.colour ?? INK });
      });
      if (opts.gap) y -= opts.gap;
    },

    /** A numbered, tinted section band (R18) — numbered as it is drawn. */
    band(title: string) {
      const height = 26;
      room(height + 30);
      y -= height + 8;
      section += 1;
      drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number: section, bold });
      y -= 10;
    },

    /** The letterhead (R18): eyebrow, big subject, quiet meta, logo, teal hairline. */
    letterhead(content: Letterhead) {
      y = drawLetterhead(page, { x: MARGIN, width: WIDTH, top: PAGE.height - MARGIN }, { regular, bold, title: titleFont }, content);
    },

    /** A label run (bold, brand) then body text (ink) on the same line, wrapping
     *  with a hanging indent so continuation lines clear the label. For a lesson's
     *  recommendation sub-line in the record ("Recommendation  <what to do next>"). */
    lead(label: string, body: string, opts: { indent?: number; gap?: number } = {}) {
      const size = 9;
      const indent = opts.indent ?? 0;
      const leading = size * 1.5;
      const labelText = winAnsi(label);
      const hang = indent + bold.widthOfTextAtSize(labelText + '  ', size);
      const avail = WIDTH - hang;
      const words = winAnsi(unwrap(body)).split(/\s+/).filter(Boolean);
      let line = '';
      let first = true;
      const flush = () => {
        room(leading);
        y -= leading;
        if (first) {
          page.drawText(labelText, { x: MARGIN + indent, y, size, font: bold, color: BRAND });
          first = false;
        }
        page.drawText(line, { x: MARGIN + hang, y, size, font: regular, color: INK });
        line = '';
      };
      for (const w of words) {
        const tried = line ? `${line} ${w}` : w;
        if (regular.widthOfTextAtSize(tried, size) <= avail) line = tried;
        else { if (line) flush(); line = w; }
      }
      flush();
      if (opts.gap) y -= opts.gap;
    },

    /** A rich-text field flattened to lines, each drawn with its marker and indent. */
    rich(doc: JSONContent | null | undefined, emptyNote: string) {
      const lines = richLines(doc);
      if (lines.length === 0) {
        api.text(emptyNote, { size: 9.5, colour: QUIET });
        return;
      }
      for (const line of lines) {
        api.text(line.text, { size: 9.5, indent: line.indent ?? 0, marker: line.marker, gap: 1 });
      }
    },
  };
  return api;
}

/** The same document produces the same bytes — pdf-lib stamps the save moment into
 *  every file, so the date is taken from the document's own prepared date, making
 *  the file a function of its content and nothing else. */
function stampDates(pdf: PDFDocument, preparedOn: string): void {
  const [day, month, year] = preparedOn.split('/').map(Number);
  const when =
    Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(Date.UTC(2000, 0, 1));
  pdf.setCreationDate(when);
  pdf.setModificationDate(when);
}

/** Every page carries the board and page n of m beneath a hairline, so a loose page
 *  still says which document it came from — the change request's rule. */
function stampFooters(pdf: PDFDocument, said: string, font: PDFFont): void {
  const pages = pdf.getPages();
  pages.forEach((page, at) => {
    const size = 7.5;
    page.drawLine({
      start: { x: MARGIN, y: MARGIN + 22 },
      end: { x: MARGIN + WIDTH, y: MARGIN + 22 },
      thickness: 0.75,
      color: RULE,
    });
    page.drawText(winAnsi(said), { x: MARGIN, y: MARGIN + 10, size, font, color: QUIET });
    const numbered = winAnsi(`Page ${at + 1} of ${pages.length}`);
    page.drawText(numbered, {
      x: MARGIN + WIDTH - font.widthOfTextAtSize(numbered, size),
      y: MARGIN + 10,
      size,
      font,
      color: QUIET,
    });
  });
}

/**
 * The after-action report, as bytes. Takes the composed content and a few facts and
 * lays them out; the caller has already worked out what it says.
 */
export async function aarPdf(facts: AarFacts, content: AarContent): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  pdf.setTitle(facts.title);
  pdf.setSubject(`After-action report — ${facts.board}`);
  pdf.setProducer('FlightDeck');
  stampDates(pdf, facts.preparedOn);

  const art = await embed(pdf, facts.logo);
  const titleFont = await embedTitleFont(pdf);
  const pen = makePen(pdf, regular, bold, { title: facts.title, logo: art }, titleFont);
  // The eyebrow names the KIND; the big subject names what the report is ABOUT —
  // the project. An AAR's own title defaults to "After-action report" (the kind),
  // so using it as the subject printed the kind twice; the project name is the
  // subject, and the kind stays in the eyebrow (Vinnie, 13 Oct 2026).
  pen.letterhead({
    eyebrow: 'After-action report',
    subject: facts.board,
    meta: [...preparedSegments(facts.preparedBy, facts.preparedOn), `Status: ${facts.status}`].join('  ·  '),
    logo: art?.art,
  });

  /* The band titles come from AAR_SECTIONS (via aarHeading), the same source the
     editor draws, so a reworded section stays identical on screen and in the file. */
  /* ── 1. What we set out to do ─────────────────────────────────────────────── */
  pen.band(aarHeading('supposed'));
  pen.rich(content.supposed, 'Not filled in yet.');
  pen.down(4);

  /* ── 2. What we delivered ─────────────────────────────────────────────────── */
  pen.band(aarHeading('actual'));
  pen.rich(content.actual, 'Not filled in yet.');
  // The reasons behind an ADVERSE variance (later than planned / over budget), each
  // tied to the figure the section already reports — the same lines the screen shows.
  {
    const schedLine = scheduleVarianceLine(content.variance);
    const costLine = costVarianceLine(content.variance);
    if (schedLine || costLine) {
      pen.down(3);
      pen.text('The reasons behind the variance', { size: 9, bold: true, colour: QUIET, gap: 2 });
      if (schedLine) {
        pen.text(`${schedLine} — ${scheduleVarianceContext(content.variance)}`, { size: 8.5, colour: QUIET, gap: 1 });
        pen.rich(content.scheduleWhy, 'Not explained.');
        pen.down(2);
      }
      if (costLine) {
        pen.text(`${costLine} — ${costVarianceContext(content.variance)}`, { size: 8.5, colour: QUIET, gap: 1 });
        pen.rich(content.costWhy, 'Not explained.');
      }
    }
  }
  pen.down(4);

  /* ── 3. What benefits we expected, and where they stand ───────────────────── */
  pen.band(aarHeading('benefits'));
  const benefits = content.benefits ?? [];
  if (benefits.length === 0) {
    pen.text('No benefits recorded.', { size: 9.5, colour: QUIET });
  } else {
    benefits.forEach((b) => {
      pen.rich(b.statement, '—');
      const bits = [AAR_BENEFIT_STATUS_LABELS[b.status]];
      if (b.monitor?.trim()) bits.push(`monitored by ${b.monitor.trim()}`);
      pen.text(bits.join('  ·  '), { size: 8.5, bold: true, colour: BRAND, gap: 1 });
      pen.down(5);
    });
  }

  /* ── 4. What moved, and why ───────────────────────────────────────────────── */
  pen.band(aarHeading('why'));
  pen.rich(content.why, 'Not filled in yet.');
  pen.down(4);

  /* ── 5. What to sustain and improve ───────────────────────────────────────── */
  pen.band(aarHeading('sustain'));
  const captured = facts.lessons ?? [];
  if (captured.length === 0 && content.sustain.length === 0) {
    pen.text(AAR_SUSTAIN_EMPTY, { size: 9.5, colour: QUIET });
  } else {
    // The lessons logged DURING the project, consolidated into the record first —
    // each like a sustain line (kind label + body), its recommendation a sub-line.
    if (captured.length) {
      pen.text('Lessons logged during the project, carried into the record:', { size: 9, colour: QUIET, gap: 3 });
      captured.forEach((l) => {
        pen.text(LESSON_KIND_LABELS[l.kind], { size: 9.5, bold: true, colour: BRAND, gap: 1 });
        pen.rich(l.body, ' ');
        if (hasRecommendation(l)) pen.lead('Recommendation', richText(l.recommendation), { gap: 0 });
        pen.down(5);
      });
    }
    // Anything the closing author added on top.
    if (content.sustain.length) {
      if (captured.length) pen.text('Added on closing:', { size: 9, bold: true, colour: QUIET, gap: 2 });
      content.sustain.forEach((line) => {
        pen.text(AAR_LINE_KIND_LABELS[line.kind], { size: 9.5, bold: true, colour: BRAND, gap: 1 });
        pen.rich(line.text, ' ');
        pen.down(4);
      });
    }
  }

  stampFooters(pdf, `${facts.board}  ·  After-action report  ·  Prepared ${wordedFromSlashed(facts.preparedOn)}`, regular);
  return pdf.save();
}
